/**
 * ============================================================
 * WEBSOCKET STATUS HOOK & INDICATOR
 * ============================================================
 * 
 * React hook and UI components for WebSocket connection status.
 * Provides real-time visibility into connection health.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import wsManager, { WS_STATES } from './websocketManager';

// ============================================================
// useWebSocketStatus Hook
// ============================================================

/**
 * Hook to access WebSocket connection status
 * @returns {object} { state, isConnected, isAvailable, reconnect, metrics }
 */
export function useWebSocketStatus() {
  const [state, setState] = useState(wsManager.state);
  const [metrics, setMetrics] = useState(wsManager.getMetrics());
  
  useEffect(() => {
    // Subscribe to state changes
    const unsubscribe = wsManager.subscribeToState((newState) => {
      setState(newState);
      setMetrics(wsManager.getMetrics());
    });
    
    // Update metrics periodically when connected
    const metricsInterval = setInterval(() => {
      setMetrics(wsManager.getMetrics());
    }, 5000);
    
    return () => {
      unsubscribe();
      clearInterval(metricsInterval);
    };
  }, []);
  
  const reconnect = useCallback(() => {
    wsManager.reconnectAttempts = 0;
    wsManager.isManualDisconnect = false;
    wsManager.connect();
  }, []);
  
  const isConnected = state === WS_STATES.CONNECTED;
  const isAvailable = state === WS_STATES.CONNECTED || state === WS_STATES.FALLBACK_POLLING;
  const isReconnecting = state === WS_STATES.RECONNECTING;
  const isOffline = state === WS_STATES.OFFLINE || state === WS_STATES.DISCONNECTED;
  const isPolling = state === WS_STATES.FALLBACK_POLLING;
  
  return {
    state,
    isConnected,
    isAvailable,
    isReconnecting,
    isOffline,
    isPolling,
    reconnect,
    metrics
  };
}


// ============================================================
// useWebSocketSubscription Hook
// ============================================================

/**
 * Subscribe to WebSocket messages on a channel
 * @param {string} channel - Channel name
 * @param {function} onMessage - Message handler callback
 */
export function useWebSocketSubscription(channel, onMessage) {
  useEffect(() => {
    if (!onMessage) return;
    
    const unsubscribe = wsManager.subscribe(channel, onMessage);
    return unsubscribe;
  }, [channel, onMessage]);
}


// ============================================================
// CONNECTION STATUS INDICATOR COMPONENT
// ============================================================

/**
 * Visual indicator for WebSocket connection status
 * Shows subtle dot indicator with tooltip
 */
export function ConnectionStatusIndicator({ 
  showLabel = false, 
  size = 'sm',
  className = '' 
}) {
  const { state, isConnected, isPolling, isReconnecting } = useWebSocketStatus();
  
  const config = useMemo(() => {
    switch (state) {
      case WS_STATES.CONNECTED:
        return {
          color: 'bg-emerald-500',
          pulseColor: 'bg-emerald-400',
          label: 'Live',
          tooltip: 'Real-time updates active'
        };
      case WS_STATES.RECONNECTING:
      case WS_STATES.CONNECTING:
        return {
          color: 'bg-amber-500',
          pulseColor: 'bg-amber-400',
          label: 'Reconnecting',
          tooltip: 'Reconnecting to server...',
          animate: true
        };
      case WS_STATES.FALLBACK_POLLING:
        return {
          color: 'bg-blue-500',
          pulseColor: 'bg-blue-400',
          label: 'Polling',
          tooltip: 'Using polling fallback (updates every 20s)'
        };
      case WS_STATES.OFFLINE:
      case WS_STATES.DISCONNECTED:
      default:
        return {
          color: 'bg-red-500',
          pulseColor: 'bg-red-400',
          label: 'Offline',
          tooltip: 'Connection lost'
        };
    }
  }, [state]);
  
  const sizeClasses = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3'
  };
  
  const dotSize = sizeClasses[size] || sizeClasses.sm;
  
  return (
    <div 
      className={`flex items-center gap-1.5 ${className}`}
      title={config.tooltip}
      data-testid="connection-status-indicator"
    >
      <span className="relative flex">
        {/* Pulse animation for reconnecting */}
        {config.animate && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${config.pulseColor} opacity-75 animate-ping`} />
        )}
        <span className={`relative inline-flex rounded-full ${dotSize} ${config.color}`} />
      </span>
      {showLabel && (
        <span className="text-[10px] text-slate-500 font-medium">{config.label}</span>
      )}
    </div>
  );
}


// ============================================================
// CONNECTION STATUS BANNER (FOR DEGRADED STATE)
// ============================================================

/**
 * Banner that shows when connection is degraded
 * Only visible when not in CONNECTED state
 */
export function ConnectionStatusBanner({ className = '' }) {
  const { state, isConnected, reconnect } = useWebSocketStatus();
  
  // Don't show when connected
  if (isConnected) return null;
  
  const config = {
    [WS_STATES.RECONNECTING]: {
      bg: 'bg-amber-50 border-amber-200',
      text: 'text-amber-800',
      message: 'Reconnecting to server...',
      showRetry: false
    },
    [WS_STATES.CONNECTING]: {
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-800',
      message: 'Connecting...',
      showRetry: false
    },
    [WS_STATES.FALLBACK_POLLING]: {
      bg: 'bg-blue-50 border-blue-200',
      text: 'text-blue-700',
      message: 'Using polling mode. Updates may be delayed.',
      showRetry: true
    },
    [WS_STATES.OFFLINE]: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-800',
      message: 'Connection lost. Some features may be unavailable.',
      showRetry: true
    },
    [WS_STATES.DISCONNECTED]: {
      bg: 'bg-slate-50 border-slate-200',
      text: 'text-slate-600',
      message: 'Not connected',
      showRetry: true
    }
  };
  
  const currentConfig = config[state] || config[WS_STATES.DISCONNECTED];
  
  return (
    <div 
      className={`px-3 py-2 border rounded-lg flex items-center justify-between gap-3 ${currentConfig.bg} ${className}`}
      data-testid="connection-status-banner"
    >
      <div className="flex items-center gap-2">
        <ConnectionStatusIndicator size="sm" />
        <span className={`text-xs ${currentConfig.text}`}>
          {currentConfig.message}
        </span>
      </div>
      {currentConfig.showRetry && (
        <button
          onClick={reconnect}
          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
        >
          Retry
        </button>
      )}
    </div>
  );
}


// ============================================================
// EXPORTS
// ============================================================

export { WS_STATES };
export default useWebSocketStatus;
